import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../utils/api";
import { usePlans } from "../plans/PlansContext";
import Icon from "./ui/Icon";
import "./ChatWidget.css";

// The panel's assistant, after the analytics panel's ChatWidget: a button at
// the bottom right opens a chat window. The conversation lives on the API
// (inventory-api /chat); only the new message and what's on screen go up.
// When the assistant changes a plan, the plans are reloaded so the screen
// shows it.

const STARTERS = [
  "Which HbbTV channels do we have in Germany?",
  "Summarise the inventory by territory and format",
  "List the 5 cheapest channels in GB",
  "What's in my latest media plan?",
];

/* ---------- a little markdown: bold, code, lists, panel links ---------- */

// Only links into the panel become links; anything else stays text
function renderInline(text, keyBase = "") {
  return text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g).map((part, i) => {
    const key = `${keyBase}${i}`;
    if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={key}>{part.slice(2, -2)}</strong>;
    if (/^`[^`]+`$/.test(part)) return <code key={key}>{part.slice(1, -1)}</code>;
    const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (link) {
      return /^\/(inventory|plans)\b/.test(link[2]) ? (
        <Link key={key} to={link[2]}>
          {link[1]}
        </Link>
      ) : (
        link[1]
      );
    }
    return part;
  });
}

function Markdown({ text }) {
  const blocks = [];
  let list = null;
  const flush = () => {
    if (list) blocks.push(list);
    list = null;
  };
  (text || "").split("\n").forEach((raw) => {
    const line = raw.trimEnd();
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (bullet || numbered) {
      const type = bullet ? "ul" : "ol";
      if (!list || list.type !== type) {
        flush();
        list = { type, items: [] };
      }
      list.items.push((bullet || numbered)[1]);
    } else if (line.trim()) {
      flush();
      const heading = line.match(/^#{1,4}\s+(.*)$/);
      blocks.push({ type: heading ? "h" : "p", text: heading ? heading[1] : line });
    }
  });
  flush();

  return blocks.map((b, i) => {
    if (b.type === "ul" || b.type === "ol") {
      const List = b.type;
      return (
        <List key={i}>
          {b.items.map((it, j) => (
            <li key={j}>{renderInline(it, `${i}-${j}-`)}</li>
          ))}
        </List>
      );
    }
    if (b.type === "h") return <p key={i} className="chat-h">{renderInline(b.text, `${i}-`)}</p>;
    return <p key={i}>{renderInline(b.text, `${i}-`)}</p>;
  });
}

const timeOf = (at) =>
  new Date(at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

// What's on screen, so "this channel" and "this plan" mean something
function screenContext(location) {
  const params = new URLSearchParams(location.search);
  const plan = location.pathname.match(/^\/plans\/([^/]+)/);
  return {
    page: location.pathname,
    channelId: params.get("channel") || "",
    planId: plan ? decodeURIComponent(plan[1]) : "",
  };
}

export default function ChatWidget() {
  const location = useLocation();
  const { reload: reloadPlans } = usePlans();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [ready, setReady] = useState(true);
  const [quota, setQuota] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  // null until the API says; false hides the assistant altogether
  const [allowed, setAllowed] = useState(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  // The conversation so far. Loaded once on page load: the answer also says
  // whether this user may use the assistant at all (403 = not for them).
  const loadHistory = useCallback(async () => {
    setLoadError("");
    try {
      const res = await api.get("/chat/history");
      setMessages(res.data.messages || []);
      setQuota(res.data.quota || null);
      setReady(res.data.ready !== false);
      setAllowed(true);
      setLoaded(true);
    } catch (err) {
      if (err.response?.status === 403) setAllowed(false);
      else {
        setAllowed(true);
        setLoadError("Couldn't load the conversation.");
      }
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  // Keep the newest message in view
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !document.querySelector(".modal-backdrop, .drawer-backdrop")) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const quotaGone = quota && quota.limit > 0 && quota.remaining === 0;

  const send = async (raw) => {
    const text = raw.trim();
    if (!text || sending || quotaGone) return;
    setInput("");
    setSending(true);
    setMessages((list) => [...list, { role: "user", content: text, at: Date.now() }]);
    try {
      const res = await api.post("/chat", { message: text, context: screenContext(location) });
      setMessages((list) => [
        ...list,
        { role: "assistant", content: res.data.reply, id: res.data.messageId, at: Date.now() },
      ]);
      if (res.data.quota) setQuota(res.data.quota);
      if ((res.data.changed || []).includes("plans")) reloadPlans();
    } catch (err) {
      const data = err.response?.data || {};
      if (data.quota) setQuota(data.quota);
      if (err.response?.status === 503) setReady(false);
      setMessages((list) => [
        ...list,
        {
          role: "assistant",
          content: data.error || "The assistant couldn't answer right now. Try again in a moment.",
          at: Date.now(),
          failed: true,
        },
      ]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  // Pressing the same thumb again clears it
  const rate = async (id, rating) => {
    const before = messages.find((m) => m.id === id)?.rating || "";
    const next = before === rating ? "" : rating;
    const apply = (value) => setMessages((list) => list.map((m) => (m.id === id ? { ...m, rating: value } : m)));
    apply(next);
    try {
      await api.post("/chat/feedback", { messageId: id, rating: next });
    } catch {
      apply(before);
    }
  };

  const clear = async () => {
    setConfirmClear(false);
    const before = messages;
    setMessages([]);
    try {
      await api.delete("/chat/history");
    } catch {
      setMessages(before);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  const showQuota = quota && quota.limit > 0 && quota.remaining <= quota.limit / 2;

  if (!allowed) return null;

  return (
    <>
      {open && (
        <section className="chat" role="dialog" aria-label="Adventory assistant">
          <header className="chat-head">
            <span className="chat-title">
              <img src="/admongrel-mark.webp" alt="" />
              Adventory assistant
            </span>
            <span className="chat-head-tools">
              {messages.length > 0 &&
                (confirmClear ? (
                  <span className="chat-confirm">
                    Clear the conversation?
                    <button type="button" className="btn-link" onClick={clear}>
                      Clear
                    </button>
                    <button type="button" className="btn-link" onClick={() => setConfirmClear(false)}>
                      Keep
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="icon-btn"
                    title="Clear the conversation"
                    aria-label="Clear the conversation"
                    onClick={() => setConfirmClear(true)}
                  >
                    <Icon name="trash" />
                  </button>
                ))}
              <button type="button" className="icon-btn" aria-label="Close" onClick={() => setOpen(false)}>
                <Icon name="x" />
              </button>
            </span>
          </header>

          <div className="chat-list" ref={listRef}>
            {!loaded && !loadError && <p className="chat-note">Loading…</p>}
            {loadError && (
              <p className="chat-note">
                {loadError}{" "}
                <button type="button" className="btn-link" onClick={loadHistory}>
                  Try again
                </button>
              </p>
            )}

            {loaded && messages.length === 0 && (
              <div className="chat-empty">
                <p>
                  Ask about channels, prices and territories, or ask for a media plan. I only see the channels and
                  plans you can see in the panel.
                </p>
                <div className="chat-starters">
                  {STARTERS.map((s) => (
                    <button key={s} type="button" disabled={!ready || quotaGone} onClick={() => send(s)}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={m.id || i} className={`chat-msg chat-${m.role}${m.failed ? " is-failed" : ""}`}>
                <div className="chat-bubble">
                  {m.role === "assistant" ? <Markdown text={m.content} /> : <p>{m.content}</p>}
                </div>
                <div className="chat-meta">
                  <span>{timeOf(m.at)}</span>
                  {m.role === "assistant" && m.id && (
                    <span className="chat-rate">
                      <button
                        type="button"
                        className={m.rating === "up" ? "is-on" : ""}
                        aria-label="Good answer"
                        aria-pressed={m.rating === "up"}
                        onClick={() => rate(m.id, "up")}
                      >
                        <Icon name="thumbUp" size={13} />
                      </button>
                      <button
                        type="button"
                        className={m.rating === "down" ? "is-on" : ""}
                        aria-label="Bad answer"
                        aria-pressed={m.rating === "down"}
                        onClick={() => rate(m.id, "down")}
                      >
                        <Icon name="thumbDown" size={13} />
                      </button>
                    </span>
                  )}
                </div>
              </div>
            ))}

            {sending && (
              <div className="chat-msg chat-assistant">
                <div className="chat-bubble chat-typing" aria-label="Thinking">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
          </div>

          {!ready && (
            <p className="chat-banner">The assistant isn't set up yet. Ask an admin to fill in the API's [azure] settings.</p>
          )}
          {ready && showQuota && (
            <p className="chat-banner">
              {quotaGone
                ? "You've used today's questions. They come back at midnight."
                : `${quota.remaining} of ${quota.limit} questions left today.`}
            </p>
          )}

          <form
            className="chat-form"
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
          >
            <textarea
              ref={inputRef}
              rows={1}
              value={input}
              maxLength={2000}
              placeholder={quotaGone ? "No questions left today" : "Ask about the inventory or a plan…"}
              aria-label="Your question"
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={!ready || quotaGone}
            />
            <button
              type="submit"
              className="chat-send"
              aria-label="Send"
              disabled={!input.trim() || sending || !ready || quotaGone}
            >
              <Icon name="send" size={18} />
            </button>
          </form>
        </section>
      )}

      <button
        type="button"
        className={`chat-fab${open ? " is-open" : ""}`}
        aria-label={open ? "Close the assistant" : "Open the assistant"}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name={open ? "x" : "chat"} size={22} />
      </button>
    </>
  );
}
